/**
 * Studio Name Resolution Utility
 * 
 * Handles the priority hierarchy for studio names:
 * 1. Studio owner's claimed display name (highest priority)
 * 2. Admin-set primary studio name
 * 3. Original competition registration name (fallback)
 * 
 * Also handles:
 * - Studio collaborations (multiple studios working together)
 * - Discipline-specific studio assignments
 */

import { collection, getDocs, doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';

// Cache for studio display names and primary studios
let studioDisplayNamesCache = new Map();
let primaryStudiosCache = new Map();
let collaborationsCache = new Map();
let disciplineStudiosCache = new Map();
let cacheLastUpdated = 0;
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

/**
 * Load studio display names from claimed studio profiles
 */
const loadStudioDisplayNames = async () => {
  try {
    const studioProfilesSnapshot = await getDocs(collection(db, 'studioProfiles'));
    const displayNames = new Map();
    
    studioProfilesSnapshot.forEach((doc) => {
      const profile = doc.data();
      if (profile.displayName && profile.claimed) {
        // Map both the original name and any variations to the display name
        displayNames.set(doc.id, profile.displayName);
        
        // Also map common variations
        if (profile.alternativeNames) {
          profile.alternativeNames.forEach(altName => {
            displayNames.set(altName.toLowerCase().trim(), profile.displayName);
          });
        }
      }
    });
    
    return displayNames;
  } catch (error) {
    console.error('Error loading studio display names:', error);
    return new Map();
  }
};

/**
 * Load primary studio preferences from dancer profiles
 */
const loadPrimaryStudios = async () => {
  try {
    const dancerProfilesSnapshot = await getDocs(collection(db, 'dancerProfiles'));
    const primaryStudios = new Map();
    
    dancerProfilesSnapshot.forEach((doc) => {
      const profile = doc.data();
      if (profile.primaryStudio) {
        primaryStudios.set(doc.id, profile.primaryStudio);
      }
    });
    
    return primaryStudios;
  } catch (error) {
    console.error('Error loading primary studios:', error);
    return new Map();
  }
};

/**
 * Load studio collaborations from dancer profiles
 */
const loadStudioCollaborations = async () => {
  try {
    const dancerProfilesSnapshot = await getDocs(collection(db, 'dancerProfiles'));
    const collaborations = new Map();
    
    dancerProfilesSnapshot.forEach((doc) => {
      const profile = doc.data();
      if (profile.studioCollaboration && profile.studioCollaboration.isActive) {
        collaborations.set(doc.id, profile.studioCollaboration);
      }
    });
    
    return collaborations;
  } catch (error) {
    console.error('Error loading studio collaborations:', error);
    return new Map();
  }
};

/**
 * Load discipline-specific studios from dancer profiles
 */
const loadDisciplineStudios = async () => {
  try {
    const dancerProfilesSnapshot = await getDocs(collection(db, 'dancerProfiles'));
    const disciplineStudios = new Map();
    
    dancerProfilesSnapshot.forEach((doc) => {
      const profile = doc.data();
      if (profile.disciplineStudios && Object.keys(profile.disciplineStudios).length > 0) {
        disciplineStudios.set(doc.id, profile.disciplineStudios);
      }
    });
    
    return disciplineStudios;
  } catch (error) {
    console.error('Error loading discipline studios:', error);
    return new Map();
  }
};

/**
 * Update cache if needed
 */
const updateCacheIfNeeded = async () => {
  const now = Date.now();
  if (now - cacheLastUpdated > CACHE_DURATION) {
    const [displayNames, primaryStudios, collaborations, disciplineStudios] = await Promise.all([
      loadStudioDisplayNames(),
      loadPrimaryStudios(),
      loadStudioCollaborations(),
      loadDisciplineStudios()
    ]);
    
    studioDisplayNamesCache = displayNames;
    primaryStudiosCache = primaryStudios;
    collaborationsCache = collaborations;
    disciplineStudiosCache = disciplineStudios;
    cacheLastUpdated = now;
  }
};

/**
 * Normalize studio name for comparison
 */
const normalizeStudioName = (name) => {
  if (!name) return '';
  return name.toLowerCase().trim();
};

/**
 * Find studio display name by checking various name formats
 */
const findStudioDisplayName = (originalName) => {
  if (!originalName) return null;
  
  const normalized = normalizeStudioName(originalName);
  
  // Direct match
  if (studioDisplayNamesCache.has(normalized)) {
    return studioDisplayNamesCache.get(normalized);
  }
  
  // Check if any cached studio names contain or are contained in the original name
  for (const [cachedName, displayName] of studioDisplayNamesCache.entries()) {
    const cachedNormalized = normalizeStudioName(cachedName);
    
    // Check for partial matches (both ways)
    if (cachedNormalized.includes(normalized) || normalized.includes(cachedNormalized)) {
      // Additional check: make sure it's a meaningful match (not just 1-2 characters)
      if (Math.min(cachedNormalized.length, normalized.length) > 3) {
        return displayName;
      }
    }
  }
  
  return null;
};

/**
 * Resolve studio name with priority hierarchy and special cases
 * @param {string} originalStudioName - The original studio name from competition
 * @param {string} dancerName - The dancer's name to check for special assignments
 * @param {string} discipline - The dance discipline (for discipline-specific studios)
 * @returns {Promise<string>} The resolved studio name
 */
export const resolveStudioName = async (originalStudioName, dancerName = null, discipline = null) => {
  if (!originalStudioName) return 'No Club';
  
  await updateCacheIfNeeded();
  
  // Special case 1: Check for discipline-specific studio assignment
  if (dancerName && discipline && disciplineStudiosCache.has(dancerName)) {
    const disciplineStudios = disciplineStudiosCache.get(dancerName);
    if (disciplineStudios[discipline]) {
      const disciplineStudio = disciplineStudios[discipline];
      // Resolve the discipline-specific studio name through the normal hierarchy
      const resolvedDisciplineStudio = await resolveStudioNameInternal(disciplineStudio, dancerName);
      return `${resolvedDisciplineStudio} (${discipline})`;
    }
  }
  
  // Special case 2: Check for studio collaboration
  if (dancerName && collaborationsCache.has(dancerName)) {
    const collaboration = collaborationsCache.get(dancerName);
    const resolvedStudios = await Promise.all(
      collaboration.studios.map(studio => resolveStudioNameInternal(studio, dancerName))
    );
    const collaborationName = resolvedStudios.join(' + ');
    if (collaboration.disciplines) {
      return `${collaborationName} (${collaboration.disciplines})`;
    }
    return collaborationName;
  }
  
  // Normal resolution
  return await resolveStudioNameInternal(originalStudioName, dancerName);
};

/**
 * Internal studio name resolution (without special cases)
 */
const resolveStudioNameInternal = async (originalStudioName, dancerName = null) => {
  if (!originalStudioName) return 'No Club';
  
  // Priority 1: Studio owner's claimed display name
  const studioDisplayName = findStudioDisplayName(originalStudioName);
  if (studioDisplayName) {
    return studioDisplayName;
  }
  
  // Priority 2: Admin-set primary studio name for this dancer
  if (dancerName && primaryStudiosCache.has(dancerName)) {
    const primaryStudio = primaryStudiosCache.get(dancerName);
    
    // Check if the primary studio matches the original studio (allowing for variations)
    const normalizedOriginal = normalizeStudioName(originalStudioName);
    const normalizedPrimary = normalizeStudioName(primaryStudio);
    
    // If they're similar enough, use the primary studio name
    if (normalizedOriginal.includes(normalizedPrimary) || 
        normalizedPrimary.includes(normalizedOriginal) ||
        calculateSimilarity(normalizedOriginal, normalizedPrimary) > 70) {
      return primaryStudio;
    }
  }
  
  // Priority 3: Original competition registration name (fallback)
  return originalStudioName;
};

/**
 * Get comprehensive studio information for a dancer
 * @param {string} dancerName - The dancer's name
 * @returns {Promise<Object>} Studio information including collaborations and discipline mappings
 */
export const getDancerStudioInfo = async (dancerName) => {
  if (!dancerName) return null;
  
  await updateCacheIfNeeded();
  
  const info = {
    primaryStudio: null,
    collaboration: null,
    disciplineStudios: null,
    hasSpecialAssignments: false
  };
  
  // Get primary studio
  if (primaryStudiosCache.has(dancerName)) {
    info.primaryStudio = primaryStudiosCache.get(dancerName);
  }
  
  // Get collaboration
  if (collaborationsCache.has(dancerName)) {
    info.collaboration = collaborationsCache.get(dancerName);
    info.hasSpecialAssignments = true;
  }
  
  // Get discipline-specific studios
  if (disciplineStudiosCache.has(dancerName)) {
    info.disciplineStudios = disciplineStudiosCache.get(dancerName);
    info.hasSpecialAssignments = true;
  }
  
  return info;
};

/**
 * Get display name for studio representation on dancer profile
 * @param {string} dancerName - The dancer's name
 * @returns {Promise<Array>} Array of studio representations with disciplines
 */
export const getDancerStudioDisplayNames = async (dancerName) => {
  if (!dancerName) return [];
  
  const studioInfo = await getDancerStudioInfo(dancerName);
  if (!studioInfo) return [];
  
  const representations = [];
  
  // Add collaboration if exists
  if (studioInfo.collaboration) {
    const resolvedStudios = await Promise.all(
      studioInfo.collaboration.studios.map(studio => resolveStudioNameInternal(studio, dancerName))
    );
    const collaborationName = resolvedStudios.join(' + ');
    representations.push({
      studioName: collaborationName,
      type: 'collaboration',
      disciplines: studioInfo.collaboration.disciplines || 'All disciplines'
    });
  }
  
  // Add discipline-specific studios if exist
  if (studioInfo.disciplineStudios) {
    for (const [discipline, studio] of Object.entries(studioInfo.disciplineStudios)) {
      const resolvedStudio = await resolveStudioNameInternal(studio, dancerName);
      representations.push({
        studioName: resolvedStudio,
        type: 'discipline-specific',
        disciplines: discipline
      });
    }
  }
  
  // If no special assignments, add primary studio or indicate standard representation
  if (!studioInfo.hasSpecialAssignments && studioInfo.primaryStudio) {
    representations.push({
      studioName: studioInfo.primaryStudio,
      type: 'primary',
      disciplines: 'All disciplines'
    });
  }
  
  return representations;
};

/**
 * Calculate similarity between two strings (for fuzzy matching)
 */
const calculateSimilarity = (str1, str2) => {
  if (str1 === str2) return 100;
  if (str1.length === 0 || str2.length === 0) return 0;
  
  const matrix = Array(str2.length + 1).fill(null).map(() => Array(str1.length + 1).fill(null));
  
  for (let i = 0; i <= str1.length; i++) {
    matrix[0][i] = i;
  }
  
  for (let j = 0; j <= str2.length; j++) {
    matrix[j][0] = j;
  }
  
  for (let j = 1; j <= str2.length; j++) {
    for (let i = 1; i <= str1.length; i++) {
      const indicator = str1[i - 1] === str2[j - 1] ? 0 : 1;
      matrix[j][i] = Math.min(
        matrix[j][i - 1] + 1,
        matrix[j - 1][i] + 1,
        matrix[j - 1][i - 1] + indicator
      );
    }
  }
  
  const distance = matrix[str2.length][str1.length];
  const maxLength = Math.max(str1.length, str2.length);
  return Math.round(((maxLength - distance) / maxLength) * 100);
};

/**
 * Batch resolve studio names for multiple dancers/studios
 * @param {Array} items - Array of objects with originalStudioName and dancerName
 * @returns {Promise<Array>} Array of resolved studio names
 */
export const batchResolveStudioNames = async (items) => {
  await updateCacheIfNeeded();
  
  return Promise.all(
    items.map(({ originalStudioName, dancerName }) => 
      resolveStudioName(originalStudioName, dancerName)
    )
  );
};

/**
 * Clear cache (useful for testing or after major updates)
 */
export const clearStudioNameCache = () => {
  studioDisplayNamesCache.clear();
  primaryStudiosCache.clear();
  cacheLastUpdated = 0;
};

/**
 * Get all studio display names (for admin purposes)
 */
export const getAllStudioDisplayNames = async () => {
  await updateCacheIfNeeded();
  return new Map(studioDisplayNamesCache);
};

/**
 * Get all primary studio settings (for admin purposes)
 */
export const getAllPrimaryStudios = async () => {
  await updateCacheIfNeeded();
  return new Map(primaryStudiosCache);
};
